<!--
  The admin's guest list: a list that saves every row as you type, like
  /new/media. Adds, edits, offline payments and removals happen in place —
  no dialogs. Open by default; the summary line stays as the disclosure.
-->
<script>
  import { getContext, tick, untrack } from 'svelte';
  import { nanoid } from 'nanoid';
  import t, { LANG } from '#lib/i18n.svelte.js';
  import { toast } from '#lib/toast.svelte.js';
  import { format_price, format_amount_input, parse_amount_input, format_currency_symbol, format_utc_date_as_local, generate_registrations_csv, get_registration_payment_state } from '#lib/util.js';
  import { get_event_capacity } from '#lib/event_capacity.js';
  import { submit_registration_changes, record_registration_payment, get_attendee_errors, translate_attendee_errors } from '#lib/event_registration_client.js';
  import TextInput from '#lib/TextInput.svelte';
  import DateInput from '#lib/DateInput.svelte';
  import SegmentedControl from '#lib/SegmentedControl.svelte';
  import FormLabel from '#lib/FormLabel.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import EventChip from '#lib/EventChip.svelte';
  import Icon from '#lib/Icon.svelte';
  import '#lib/system-styles/event-ui.css';

  let {
    entry,
    additional_entry_data,
    /** Opens the parent's e-mail dialog */
    on_email_all,
    /** Refetches registrations and seat counts after a change */
    onrefresh,
  } = $props();

  // The list is the admin's working surface: open unless they collapse it
  let is_open = $state(true);

  // The site's two button surfaces: actions and their quieter counterparts
  const site_settings = getContext('site_settings');
  const primary_button = `brand ${site_settings.primary_control_surface}`;
  const secondary_button = `brand ${site_settings.secondary_control_surface}`;
  const destructive_button = `system-destructive ${site_settings.primary_control_surface}`;

  const FIELDS = ['name', 'email', 'notes'];
  const CHIP_TONES = { paid: 'ok', unpaid: 'warn', comp: 'neutral', pending: 'neutral', processing: 'neutral', refunded: 'neutral' };
  // States whose chip opens the payment editor. A refunded seat is settled in
  // Stripe, a settling payment (processing) may not be booked over, and a
  // row that owes nothing (free) shows no chip at all.
  const EDITABLE_PAYMENT_STATES = new Set(['paid', 'unpaid', 'comp', 'pending']);

  let server_registrations = $derived(additional_entry_data?.event_registrations ?? []);
  let capacity = $derived(get_event_capacity(entry, additional_entry_data));
  let has_payments = $derived(entry.payment_required === true);
  let currency = $derived(entry.currency_code);
  let currency_symbol = $derived(format_currency_symbol(currency, LANG));

  /**
   * One row per registration plus rows the admin is still typing.
   * fields: what the inputs show; touched: fields edited since the last
   * server truth; save_state: idle | saving | saved | failed
   *
   * Seeded from the prop rather than left to the sync effect below: effects
   * don't run while rendering on the server, so an empty start would ship the
   * "no registrations yet" state and pop the rows in only at hydration.
   * The row keys are derived from the registration ids, so both renders match.
   */
  // svelte-ignore state_referenced_locally (the initial value is what we want; the effect below tracks the rest)
  let rows = $state((additional_entry_data?.event_registrations ?? []).map(registration => create_row(registration)));
  /** Registration id whose payment editor is open (one at a time) */
  let open_payment_id = $state(null);
  let payment = $state({ method: 'bank_transfer', amount: '', paid_at: '', notify: true, busy: false, error: '' });

  function create_row(registration = null) {
    return {
      key: registration ? `reg_${registration.event_registration_id}` : nanoid(),
      registration,
      fields: { name: registration?.name ?? '', email: registration?.email ?? '', notes: registration?.notes ?? '' },
      touched: {},
      save_state: 'idle',
      pending_save: false,
      confirm_remove: false,
    };
  }

  // Later server truth flows into every untouched field; unsaved new rows survive reloads
  $effect(() => {
    const server_rows = server_registrations;
    untrack(() => {
      const by_id = new Map(rows.filter(row => row.registration).map(row => [row.registration.event_registration_id, row]));
      const synced = server_rows.map(registration => {
        const row = by_id.get(registration.event_registration_id);
        if (!row) return create_row(registration);
        row.registration = registration;
        for (const field of FIELDS) {
          if (!row.touched[field]) row.fields[field] = registration[field] ?? '';
        }
        return row;
      });
      rows = [...synced, ...rows.filter(row => !row.registration)];
    });
  });

  // What the list knows right now — saved rows included — feeds the summary,
  // the CSV and the e-mail tools, not the page-load snapshot
  let registrations = $derived(rows.filter(row => row.registration).map(row => row.registration));
  let confirmed_rows = $derived(rows.filter(row => row.registration?.status !== 'waitlist'));
  let waitlist_rows = $derived(rows.filter(row => row.registration?.status === 'waitlist'));

  let payment_counts = $derived.by(() => {
    const counts = { paid: 0, unpaid: 0, pending: 0 };
    for (const reg of registrations) {
      const state = get_registration_payment_state(reg, entry);
      // A settling payment is still an open checkout to the summary
      const bucket = state === 'processing' ? 'pending' : state;
      if (bucket in counts) counts[bucket] += 1;
    }
    return counts;
  });

  let summary = $derived(
    [
      capacity.is_unlimited
        ? (capacity.seats_taken === 1
          ? t('registered_guests_count_singular')
          : t('registered_guests_count', { count: capacity.seats_taken }))
        : t('guest_list_summary_seats', { taken: capacity.seats_taken, total: capacity.max_attendees }),
      has_payments && payment_counts.paid > 0 ? t('guest_list_summary_paid', { count: payment_counts.paid }) : '',
      has_payments && payment_counts.unpaid > 0 ? t('guest_list_summary_unpaid', { count: payment_counts.unpaid }) : '',
      has_payments && payment_counts.pending > 0 ? t('guest_list_summary_pending', { count: payment_counts.pending }) : '',
      capacity.waitlist_count > 0 ? t('guest_list_summary_waitlist', { count: capacity.waitlist_count }) : '',
    ].filter(Boolean).join(', ')
  );

  // ---------------------------------------------------------------------------
  // Autosave
  // ---------------------------------------------------------------------------
  function is_row_valid(row) {
    return Object.keys(get_attendee_errors(row.fields)).length === 0;
  }

  function is_row_dirty(row) {
    if (!row.registration) return Object.keys(row.touched).length > 0;
    return FIELDS.some(field => (row.registration[field] ?? '') !== row.fields[field]);
  }

  async function save_row(row) {
    if (row.save_state === 'saving') { row.pending_save = true; return; }
    if (!is_row_dirty(row) || !is_row_valid(row)) return;
    row.save_state = 'saving';

    const attendee = { name: row.fields.name.trim(), email: row.fields.email.trim(), notes: row.fields.notes };
    // What the fields held at send time: a field the admin edits again while
    // the save is in flight stays touched, so its newer value is not lost.
    const sent = { name: row.fields.name, email: row.fields.email, notes: row.fields.notes };
    const result = row.registration
      ? await submit_registration_changes(entry.entry_id, {
          changed: [{ event_registration_id: row.registration.event_registration_id, ...attendee }]
        })
      : await submit_registration_changes(entry.entry_id, { added: [attendee] });

    if (result.success && result.registrations?.[0]) {
      const was_new = !row.registration;
      row.registration = result.registrations[0];
      row.error = '';
      for (const field of FIELDS) {
        if (row.fields[field] === sent[field]) delete row.touched[field];
      }
      row.save_state = 'saved';
      setTimeout(() => { if (row.save_state === 'saved') row.save_state = 'idle'; }, 2000);
      if (was_new) onrefresh?.(); // seat counts changed
    } else if (result.success) {
      // The update matched no row: the guest was removed elsewhere meanwhile
      row.save_state = 'failed';
      row.error = t('event_registrations_update_error');
      onrefresh?.();
    } else {
      row.save_state = 'failed';
      row.error = t(result.error || 'event_registrations_update_error');
    }

    if (row.pending_save) {
      row.pending_save = false;
      save_row(row);
    }
  }

  function edit_field(row, field, value) {
    row.fields[field] = value;
    row.touched[field] = true;
  }

  async function add_row() {
    const row = create_row();
    rows.push(row);
    await tick();
    // The new row lands below every guest — bring it on screen before focusing it
    const input = document.getElementById(`guest_name_${row.key}`);
    input?.scrollIntoView({ block: 'center' });
    input?.focus({ preventScroll: true });
  }

  async function remove_row(row) {
    if (!row.registration) {
      rows = rows.filter(other => other !== row);
      return;
    }
    row.save_state = 'saving';
    const result = await submit_registration_changes(entry.entry_id, {
      cancelled_ids: [row.registration.event_registration_id]
    });
    if (!result.success) {
      row.save_state = 'failed';
      row.confirm_remove = false;
      toast.error(t(result.error));
      return;
    }
    rows = rows.filter(other => other !== row);
    if (open_payment_id === row.registration.event_registration_id) open_payment_id = null;
    await onrefresh?.();
  }

  // ---------------------------------------------------------------------------
  // Offline payments
  // ---------------------------------------------------------------------------
  function toggle_payment_editor(registration) {
    if (open_payment_id === registration.event_registration_id) {
      open_payment_id = null;
      return;
    }
    open_payment_id = registration.event_registration_id;
    payment = {
      method: registration.payment_method && registration.payment_method !== 'stripe' ? registration.payment_method : 'bank_transfer',
      amount: format_amount_input(
        Number.isInteger(registration.amount_paid) && registration.paid_at ? registration.amount_paid : (entry.price_amount ?? 0),
        LANG
      ),
      // A UTC timestamp; DateInput edits its date and keeps the time of day
      paid_at: registration.paid_at || new Date().toISOString(),
      // A receipt needs an address; nothing to send for guests without one
      notify: !registration.paid_at && Boolean(registration.email),
      busy: false,
      error: '',
    };
  }

  async function submit_payment(registration, { paid = true, method = payment.method } = {}) {
    // A non-empty amount that does not parse is an error, not a fall-back to the
    // event price: catch it here before it reaches the server.
    let amount_paid = 0;
    if (paid && method !== 'comp') {
      amount_paid = parse_amount_input(payment.amount, LANG);
      if (payment.amount.trim() !== '' && amount_paid === null) {
        payment.error = t('amount_invalid');
        return;
      }
    }
    payment.busy = true;
    payment.error = '';
    const payload = paid
      ? {
          event_registration_id: registration.event_registration_id,
          paid: true,
          payment_method: method,
          amount_paid,
          paid_at: payment.paid_at || new Date().toISOString(),
          notify: payment.notify && method !== 'comp',
        }
      : { event_registration_id: registration.event_registration_id, paid: false };

    const result = await record_registration_payment(entry.entry_id, payload);
    payment.busy = false;
    if (!result.success) {
      payment.error = t(result.error);
      return;
    }
    open_payment_id = null;
    toast.success(t(paid ? 'payment_recorded' : 'payment_cleared'));
    await onrefresh?.();
  }

  /** "12,50 € bezahlt" for paid rows, the state alone otherwise */
  function payment_label(registration) {
    const state = get_registration_payment_state(registration, entry);
    if (state === 'paid' && Number.isInteger(registration.amount_paid)) {
      return t('amount_paid_inline', { amount: format_price(registration.amount_paid, registration.currency || currency, LANG) });
    }
    return t(`payment_state_${state}`);
  }

  function download_csv() {
    const { blob, filename } = generate_registrations_csv(registrations, entry, t);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggle_open() {
    is_open = !is_open;
  }
</script>

<section class='flex-column gap-y g-4' id='guest-list'>

  <!---------------------------------------------------------------------------------->
  <!-------------------------- Title, summary and tools ------------------------------>
  <!---------------------------------------------------------------------------------->
  <div class='event-compact event-list-tools'>
    <!-- The heading is the disclosure: title, summary and the arrow -->
    <button type='button' onclick={toggle_open} aria-expanded={is_open} aria-controls='guest-list-rows'>
      <ButtonLabel variant='brand flush' align='start' end_icon={is_open ? 'small-arrow-up' : 'small-arrow-down'}>
        <span class='-semibold'>{t('guest_list')}</span><span class='text-2 event-aside'>{summary}</span>
      </ButtonLabel>
    </button>
    {#if is_open}
      <div class='event-tools'>
        <button type='button' onclick={add_row}>
          <ButtonLabel variant={primary_button} start_icon='plus'>{t('add_guest')}</ButtonLabel>
        </button>
        {#if registrations.length > 0}
          <button type='button' onclick={on_email_all}>
            <ButtonLabel variant={primary_button} start_icon='paperplane'>{t('email_all')}</ButtonLabel>
          </button>
          <button type='button' onclick={download_csv}>
            <ButtonLabel variant={primary_button} start_icon='download'>{t('csv')}</ButtonLabel>
          </button>
        {/if}
      </div>
    {/if}
  </div>

  {#if is_open}
    <div id='guest-list-rows' class='event-list-wrap'>
      {#if rows.length === 0}
        <p class='text-control text-2 leading-trim'>{t('guest_list_empty')}</p>
      {:else}
        <!---------------------------------------------------------------------------------->
        <!-------------------------- Rows -------------------------------------------------->
        <!---------------------------------------------------------------------------------->
        <div class='event-list event-compact' class:has-payment={has_payments}>
          <div class='event-list-head'>
            <span class='text-control event-label'>#</span>
            <span class='text-control event-label'>{t('name')}</span>
            <span class='text-control event-label'>{t('email_short')}</span>
            {#if has_payments}
              <span class='text-control event-label'>{t('payment')}</span>
            {/if}
            <span class='text-control event-label'>{t('notes')}</span>
            <span aria-hidden='true'></span>
          </div>

          {#each [confirmed_rows, waitlist_rows] as group, group_index}
            {#if group_index === 1 && group.length > 0}
              <div class='event-list-group'>
                <span class='text-control event-label'>{t('waitlist')}</span>
              </div>
            {/if}
            {#each group as row, index (row.key)}
              {@const registration = row.registration}
              {@const field_errors = translate_attendee_errors(get_attendee_errors(row.fields), t)}
              {@const row_label = row.fields.name.trim() || t('guest_numbered', { number: index + 1 })}
              {@const payment_state = registration ? get_registration_payment_state(registration, entry) : 'free'}
              {@const payment_open = registration && open_payment_id === registration.event_registration_id}
              <div
                class='event-list-row'
                class:is-dim={payment_state === 'refunded'}
                class:is-open={payment_open || (registration && row.confirm_remove)}
              >
                <span class='event-list-number text-control'>{index + 1}</span>
                <div class='event-list-field col-name'>
                  <TextInput
                    id='guest_name_{row.key}'
                    class='event-field w-full'
                    variant='brand ghostly'
                    value={row.fields.name}
                    placeholder={t('name')}
                    aria_label={`${t('guest_numbered', { number: index + 1 })}: ${t('name')}`}
                    oninput={(event) => edit_field(row, 'name', event.target.value)}
                    onchange={() => save_row(row)}
                    onenter={() => save_row(row)}
                    error={row.touched.name ? field_errors.name : undefined}
                  />
                </div>
                <div class='event-list-field col-email'>
                  <TextInput
                    class='event-field w-full'
                    variant='brand ghostly'
                    type='email'
                    inputmode='email'
                    id='guest_email_{row.key}'
                    value={row.fields.email}
                    placeholder={t('email_short')}
                    aria_label={`${row_label}: ${t('email')}`}
                    oninput={(event) => edit_field(row, 'email', event.target.value)}
                    onchange={() => save_row(row)}
                    onenter={() => save_row(row)}
                    error={row.touched.email ? field_errors.email : undefined}
                  />
                </div>
                {#if has_payments}
                  <div class='event-list-cell col-payment'>
                    {#if registration && EDITABLE_PAYMENT_STATES.has(payment_state)}
                      <!-- Pending too: a guest who paid cash while a checkout was
                           open is settled here (the server closes the session first) -->
                      <button
                        type='button'
                        class='event-list-chip'
                        onclick={() => toggle_payment_editor(registration)}
                        aria-expanded={payment_open}
                      >
                        {@render payment_chip(registration, payment_state)}
                      </button>
                    {:else if registration && payment_state !== 'free'}
                      {@render payment_chip(registration, payment_state)}
                    {/if}
                  </div>
                {/if}
                <div class='event-list-field col-notes'>
                  <TextInput
                    class='event-field w-full'
                    variant='brand ghostly'
                    id='guest_notes_{row.key}'
                    value={row.fields.notes}
                    placeholder={t('notes')}
                    aria_label={`${row_label}: ${t('notes')}`}
                    oninput={(event) => edit_field(row, 'notes', event.target.value)}
                    onchange={() => save_row(row)}
                    onenter={() => save_row(row)}
                    error={row.touched.notes ? field_errors.notes : undefined}
                  />
                </div>
                <div class='event-list-cell col-actions'>
                  {#if row.save_state === 'saving'}
                    <span class='loading-spinner' role='status' aria-label={t('saving')}></span>
                  {:else if row.save_state === 'saved'}
                    <span class='text-2' aria-label={t('saved')}><Icon name='check' /></span>
                  {:else if row.save_state === 'failed'}
                    <button type='button' onclick={() => { row.save_state = 'idle'; save_row(row); }}>
                      <ButtonLabel variant={destructive_button}>{t('retry')}</ButtonLabel>
                    </button>
                  {/if}
                  <!-- Unsaved rows vanish right away; saved guests ask first -->
                  <button
                    type='button'
                    onclick={() => { if (registration) row.confirm_remove = !row.confirm_remove; else remove_row(row); }}
                    disabled={row.save_state === 'saving'}
                    title={t('remove')}
                    aria-label={t('remove')}
                    aria-expanded={registration ? row.confirm_remove : undefined}
                  >
                    <ButtonLabel variant='brand ghostly' start_icon='trash' />
                  </button>
                </div>
              </div>

              <!-- Why the last save was refused, under the row it belongs to -->
              {#if row.save_state === 'failed' && row.error}
                <div class='event-list-panel' role='alert'>
                  <p class='text-control text-error leading-trim'>{row.error}</p>
                </div>
              {/if}

              <!---------------------------------------------------------------------------------->
              <!-------------------------- Remove confirmation ----------------------------------->
              <!---------------------------------------------------------------------------------->
              {#if registration && row.confirm_remove}
                <div class='event-list-panel'>
                  <div class='event-lines event-prompt'>
                    <p class='text-control leading-trim'>{t('confirm_remove_guest', { name: registration.name })}</p>
                    <div class='event-actions'>
                      <button type='button' onclick={() => { row.confirm_remove = false; }} disabled={row.save_state === 'saving'}>
                        <ButtonLabel variant={secondary_button}>{t('keep')}</ButtonLabel>
                      </button>
                      <button type='button' onclick={() => remove_row(row)} disabled={row.save_state === 'saving'}>
                        <ButtonLabel variant={destructive_button}>
                          {row.save_state === 'saving' ? t('saving') : t('remove')}
                        </ButtonLabel>
                      </button>
                    </div>
                  </div>
                </div>
              {/if}

              <!---------------------------------------------------------------------------------->
              <!-------------------------- Offline payment editor -------------------------------->
              <!---------------------------------------------------------------------------------->
              {#if payment_open}
                <div class='event-list-panel' role='group' aria-label={t('payment')}>
                  <div class='event-lines'>
                    {#if registration.payment_method === 'stripe'}
                      <p class='text-control text-2 leading-trim'>{t('event_registration_payment_stripe_locked')}</p>
                    {:else}
                      <div class='event-rows'>
                        <div class='event-row'>
                          <span class='text-control event-label'>{t('payment_method')}</span>
                          <div class='event-value'>
                            <SegmentedControl
                              bind:value={payment.method}
                              options={[
                                { value: 'bank_transfer', title: t('payment_method_bank_transfer') },
                                { value: 'cash', title: t('payment_method_cash') },
                                { value: 'invoice', title: t('payment_method_invoice') },
                                { value: 'comp', title: t('payment_method_comp') },
                              ]}
                            />
                          </div>
                        </div>
                        {#if payment.method !== 'comp'}
                          <div class='event-row'>
                            <FormLabel for='payment_amount_{registration.event_registration_id}'>{t('amount')}</FormLabel>
                            <div class='event-value'>
                              <label class='event-field w-8'>
                                <TextInput
                                  id='payment_amount_{registration.event_registration_id}'
                                  variant='brand flush'
                                  bind:value={payment.amount}
                                  inputmode='decimal'
                                />
                                <span class='event-unit text-control size-reduce-1'>{currency_symbol}</span>
                              </label>
                            </div>
                          </div>
                          <div class='event-row'>
                            <FormLabel for='payment_date_{registration.event_registration_id}'>{t('paid_at')}</FormLabel>
                            <div class='event-value'>
                              <DateInput
                                id='payment_date_{registration.event_registration_id}'
                                class='event-field'
                                variant='brand ghostly'
                                is_editing={true}
                                bind:value={payment.paid_at}
                                max={format_utc_date_as_local(new Date())}
                              />
                            </div>
                          </div>
                          {#if registration.email}
                            <!-- A labelled row like the ones above, the checkbox as its value -->
                            <div class='event-row'>
                              <FormLabel for='payment_notify_{registration.event_registration_id}'>{t('send_receipt')}</FormLabel>
                              <div class='event-value'>
                                <input id='payment_notify_{registration.event_registration_id}' type='checkbox' bind:checked={payment.notify} />
                              </div>
                            </div>
                          {/if}
                        {/if}
                        {#if payment.error}
                          <div class='event-row -unlabelled'>
                            <p class='text-control text-error leading-trim'>{payment.error}</p>
                          </div>
                        {/if}
                        <!-- The buttons close the form in its value column, under the fields -->
                        <div class='event-row -unlabelled'>
                          <div class='event-actions'>
                            <button type='button' onclick={() => { open_payment_id = null; }} disabled={payment.busy}>
                              <ButtonLabel variant={secondary_button}>{t('close')}</ButtonLabel>
                            </button>
                            {#if payment_state === 'paid' || payment_state === 'comp'}
                              <button type='button' onclick={() => submit_payment(registration, { paid: false })} disabled={payment.busy}>
                                <ButtonLabel variant={primary_button}>{t('mark_as_unpaid')}</ButtonLabel>
                              </button>
                            {/if}
                            <button type='button' onclick={() => submit_payment(registration)} disabled={payment.busy}>
                              <ButtonLabel variant={primary_button}>
                                {payment.busy ? t('saving') : payment.method === 'comp' ? t('mark_as_comp') : t('mark_as_paid')}
                              </ButtonLabel>
                            </button>
                          </div>
                        </div>
                      </div>
                    {/if}
                  </div>
                </div>
              {/if}
            {/each}
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</section>

{#snippet payment_chip(registration, payment_state)}
  <EventChip tone={CHIP_TONES[payment_state] ?? 'neutral'}>{payment_label(registration)}</EventChip>
{/snippet}

<style lang='postcss'>
  /* The shared spinner (animations.css), centred on the action column */
  .loading-spinner {
    margin-inline: calc((var(--input-min-height) - 14px) / 2);
  }
</style>
